/**
 * SA-1418 (A2): a second press of a deep-link button lands its section again.
 *
 * A `?panel=` press that sets the value the URL already holds changes
 * nothing, so the section's `focused` never changes and a section the person
 * closed after the first press stayed closed. Every press on the record page
 * goes through `useOpenPanel`: when the parameter already names the target it
 * sends that section a landing request instead of navigating, and the section
 * does what the first press did (opens, lands under the strip, follows the
 * layout and rings for FOCUS_FOLLOW_MS). The request is never keyed on the
 * router's location key: `?step=` and every other navigation change that key,
 * and a focused section must not re-land on them. A plain module-level
 * subscription, so no store needs an identityScope entry.
 */
import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

const listeners = new Map<string, Set<() => void>>()

/** Asks the section with this id to land again; a section that is not mounted hears nothing. */
export function requestLanding(panel: string): void {
  for (const listener of [...(listeners.get(panel) ?? [])]) listener()
}

/** Subscribes a section to its landing requests; returns the unsubscribe. */
export function onLandingRequest(panel: string, listener: () => void): () => void {
  let set = listeners.get(panel)
  if (!set) {
    set = new Set()
    listeners.set(panel, set)
  }
  set.add(listener)
  return () => {
    set.delete(listener)
    if (set.size === 0 && listeners.get(panel) === set) listeners.delete(panel)
  }
}

/** The one way a record-page control opens a section: `?panel=` with replace, or a landing request when it is already set. */
export function useOpenPanel(): (panel: string) => void {
  const [params, setSearchParams] = useSearchParams()
  const current = params.get('panel')
  return useCallback((panel: string) => {
    if (current === panel) {
      requestLanding(panel)
      return
    }
    setSearchParams((prev) => { prev.set('panel', panel); return prev }, { replace: true })
  }, [current, setSearchParams])
}
